import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Play, Pause, RotateCcw, Coffee, Zap, Clock, Star, Bell, BellOff, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { playTimerEnd, playDing, playSoundIfEnabled, playFocusAlarm, isFocusAlarmEnabled, setFocusAlarmEnabled } from '../utils/sounds';
import AmbientMixer from './AmbientMixer';
import BodyDoublingMode from './BodyDoublingMode';
import FocusPetActivity from './FocusPetActivity';
import FocusWakeUp from './FocusWakeUp';
import FocusLockOverlay from './FocusLockOverlay';
import BreakExercises from './BreakExercises';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMER_PRESETS = {
  pomodoro: { work: 25, break: 5, label: 'Pomodoro' },
  short: { work: 15, break: 3, label: 'Kort fokus' },
  long: { work: 45, break: 10, label: 'Djupt fokus' },
  custom: { work: 10, break: 5, label: 'Egen tid' },
};

export default function FocusTimer({ pet, onSessionComplete }) {
  const [timerType, setTimerType] = useState('pomodoro');
  const [isWork, setIsWork] = useState(true);
  const [timeLeft, setTimeLeft] = useState(TIMER_PRESETS.pomodoro.work * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [customMinutes, setCustomMinutes] = useState(10);
  const [alarmOn, setAlarmOn] = useState(isFocusAlarmEnabled());
  const [wakeUp, setWakeUp] = useState(null); // { pointsEarned, leveledUp } | null
  const [lockMode, setLockMode] = useState(() => {
    try { return localStorage.getItem('focus_lock_mode') === '1'; } catch { return false; }
  });
  const [showBreakExercises, setShowBreakExercises] = useState(false);
  const intervalRef = useRef(null);

  const preset = TIMER_PRESETS[timerType];
  const totalSeconds = isWork 
    ? (timerType === 'custom' ? customMinutes : preset.work) * 60 
    : preset.break * 60;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const sendNotification = useCallback((title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: pet?.image });
    }
  }, [pet]);

  const completeSession = useCallback(async () => {
    if (currentSession && isWork) {
      try {
        const { data } = await axios.post(
          `${API}/focus-sessions/${currentSession.id}/complete`,
          {},
          { withCredentials: true }
        );
        // Show wake-up celebration instead of plain toast
        setWakeUp({
          pointsEarned: data.points_earned || 0,
          leveledUp: !!data.pet_leveled_up,
        });
        if (onSessionComplete) onSessionComplete();
      } catch (e) {
        console.error('Failed to complete session:', e);
      }
    }
    setCurrentSession(null);
  }, [currentSession, isWork, onSessionComplete]);

  const handleTimerEnd = useCallback(() => {
    setIsRunning(false);
    // LOUD alarm so user actually hears when session ends
    if (isFocusAlarmEnabled()) {
      try { playFocusAlarm(); } catch { /* ignore */ }
    } else {
      playSoundIfEnabled(playTimerEnd);
    }
    // Vibrate on mobile (if supported)
    if (navigator.vibrate) {
      try { navigator.vibrate([300, 150, 300, 150, 400]); } catch { /* ignore */ }
    }

    if (isWork) {
      sendNotification('Focus Complete!', 'Time for a break! You earned points!');
      completeSession();
      setIsWork(false);
      setTimeLeft(preset.break * 60);
      setShowBreakExercises(true);
    } else {
      sendNotification('Break Over!', 'Ready to focus again?');
      toast.info('⏰ Paus över! Dags att fokusera igen!', { icon: '💪', duration: 10000 });
      setIsWork(true);
      setTimeLeft((timerType === 'custom' ? customMinutes : preset.work) * 60);
    }
  }, [isWork, preset, timerType, customMinutes, sendNotification, completeSession]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimerEnd();
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, timeLeft, handleTimerEnd]);

  const startTimer = async () => {
    // Start timer IMMEDIATELY — don't wait on anything
    setIsRunning(true);

    // Fire-and-forget: request notification permission (desktop prompt
    // can hang indefinitely if user ignores it — never block timer start)
    if ('Notification' in window && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }

    // Fire-and-forget: create backend session record (UI doesn't need to wait)
    if (!currentSession && isWork) {
      const duration = timerType === 'custom' ? customMinutes : preset.work;
      axios.post(
        `${API}/focus-sessions`,
        { session_type: timerType, duration_minutes: duration },
        { withCredentials: true }
      ).then(({ data }) => {
        setCurrentSession(data);
      }).catch((e) => {
        console.error('Failed to start session:', e);
      });
    }
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setCurrentSession(null);
    setIsWork(true);
    setTimeLeft((timerType === 'custom' ? customMinutes : preset.work) * 60);
  };

  const changeTimerType = (type) => {
    setTimerType(type);
    setIsRunning(false);
    setCurrentSession(null);
    setIsWork(true);
    if (type === 'custom') {
      setTimeLeft(customMinutes * 60);
    } else {
      setTimeLeft(TIMER_PRESETS[type].work * 60);
    }
  };

  const progress = ((totalSeconds - timeLeft) / totalSeconds) * 100;

  return (
    <div className="neo-card p-8" data-testid="focus-timer">
      {/* Timer Type Selection */}
      <div className="flex flex-wrap gap-2 mb-8 justify-center">
        {Object.entries(TIMER_PRESETS).map(([key, value]) => (
          <button
            key={key}
            onClick={() => changeTimerType(key)}
            className={`neo-btn px-4 py-2 ${
              timerType === key ? 'bg-[#A2D2FF]' : 'bg-white'
            }`}
            data-testid={`timer-type-${key}`}
          >
            {value.label}
          </button>
        ))}
      </div>

      {/* Custom Minutes Input */}
      {timerType === 'custom' && (
        <div className="flex items-center justify-center gap-4 mb-6">
          <label className="font-bold">Minuter:</label>
          <input
            type="number"
            min="1"
            max="120"
            value={customMinutes}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 10;
              setCustomMinutes(val);
              if (!isRunning && isWork) {
                setTimeLeft(val * 60);
              }
            }}
            className="neo-input w-20 text-center"
            data-testid="custom-minutes-input"
          />
        </div>
      )}

      {/* Main Timer Display */}
      <div className="relative w-64 h-64 mx-auto mb-8">
        {/* Progress Ring */}
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="128"
            cy="128"
            r="120"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="12"
          />
          <circle
            cx="128"
            cy="128"
            r="120"
            fill="none"
            stroke={isWork ? '#A2D2FF' : '#B9FBC0'}
            strokeWidth="12"
            strokeDasharray={2 * Math.PI * 120}
            strokeDashoffset={2 * Math.PI * 120 * (1 - progress / 100)}
            strokeLinecap="round"
            className="transition-all duration-1000"
          />
        </svg>
        
        {/* Timer Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="timer-display" data-testid="timer-display">{formatTime(timeLeft)}</p>
          <p className="text-lg font-semibold text-gray-600 flex items-center gap-2">
            {isWork ? (
              <>
                <Zap className="w-5 h-5 text-yellow-500" /> Fokustid
              </>
            ) : (
              <>
                <Coffee className="w-5 h-5 text-brown-500" /> Paustid
              </>
            )}
          </p>
        </div>
      </div>

      {/* Pet Encouragement — animated while work-focus running, static otherwise */}
      {pet && isRunning && isWork ? (
        <FocusPetActivity pet={pet} isRunning={isRunning} isWork={isWork} />
      ) : pet && (
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full border-3 border-black overflow-hidden shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
            <img src={pet.image} alt={pet.name} className="w-full h-full object-cover" />
          </div>
          <div className="neo-card p-3 bg-[#FDF0D5] max-w-xs">
            <p className="font-semibold text-sm">
              {isWork 
                ? `${pet.name} säger: Nu kör vi, fokusera!`
                : `${pet.name} säger: Bra jobbat! Njut av pausen!`
              }
            </p>
          </div>
        </div>
      )}

      {/* Ambient Sound Mixer */}
      <AmbientMixer isTimerRunning={isRunning} />

      {/* Body-doubling mode */}
      <div className="mt-4">
        <BodyDoublingMode pet={pet} isRunning={isRunning && isWork} />
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-4">
        {!isRunning ? (
          <button
            onClick={startTimer}
            className="neo-btn px-8 py-4 bg-[#B9FBC0] flex items-center gap-2 text-lg"
            data-testid="start-timer-btn"
          >
            <Play className="w-6 h-6" fill="currentColor" />
            <span>{currentSession ? 'Fortsätt' : 'Starta'}</span>
          </button>
        ) : (
          <button
            onClick={pauseTimer}
            className="neo-btn px-8 py-4 bg-[#FFC8DD] flex items-center gap-2 text-lg"
            data-testid="pause-timer-btn"
          >
            <Pause className="w-6 h-6" fill="currentColor" />
            <span>Pausa</span>
          </button>
        )}
        <button
          onClick={resetTimer}
          className="neo-btn px-4 py-4 bg-white"
          data-testid="reset-timer-btn"
        >
          <RotateCcw className="w-6 h-6" />
        </button>
      </div>

      {/* Points Info */}
      {isWork && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 flex items-center justify-center gap-1">
            <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
            Complete this session to earn <strong>{(timerType === 'custom' ? customMinutes : preset.work) * 2} points</strong>!
          </p>
        </div>
      )}

      {/* Alarm controls */}
      <div className="mt-4 flex items-center justify-center gap-2 flex-wrap text-xs">
        <button
          onClick={() => {
            const next = !alarmOn;
            setAlarmOn(next);
            setFocusAlarmEnabled(next);
            toast.info(next ? '🔔 Alarm PÅ' : '🔕 Alarm AV');
          }}
          className={`neo-btn px-3 py-1.5 flex items-center gap-1.5 ${alarmOn ? 'bg-[#FF6B6B] text-white' : 'bg-white'}`}
          data-testid="focus-alarm-toggle"
          title={alarmOn ? 'Alarm PÅ när tiden är slut' : 'Alarm AV (mjukt ljud)'}
        >
          {alarmOn ? <Bell className="w-3.5 h-3.5" fill="currentColor" /> : <BellOff className="w-3.5 h-3.5" />}
          {alarmOn ? 'Alarm PÅ' : 'Alarm AV'}
        </button>
        <button
          onClick={() => { try { playFocusAlarm(); toast.success('🔔 Såhär låter alarmet!'); } catch { /* ignore */ } }}
          className="neo-btn px-3 py-1.5 bg-[#FDF0D5] flex items-center gap-1.5"
          data-testid="focus-alarm-test"
          title="Testa alarmet"
        >
          <Volume2 className="w-3.5 h-3.5" />
          Testa
        </button>
        <button
          onClick={async () => {
            if ('Notification' in window) {
              const perm = await Notification.requestPermission();
              if (perm === 'granted') toast.success('🔔 Notiser tillåtna');
              else toast.info('Notiser nekades (alarmet fungerar ändå)');
            }
          }}
          className="neo-btn px-3 py-1.5 bg-[#A2D2FF] flex items-center gap-1.5"
          data-testid="focus-notification-perm"
          title="Tillåt webbläsar-notiser"
        >
          <Bell className="w-3.5 h-3.5" />
          Notiser
        </button>
        <label className="neo-btn px-3 py-1.5 bg-[#C8B6FF] flex items-center gap-1.5 cursor-pointer" data-testid="focus-lock-toggle" title="Fullskärms-låsläge under fokus">
          <input
            type="checkbox"
            checked={lockMode}
            onChange={(e) => {
              setLockMode(e.target.checked);
              try { localStorage.setItem('focus_lock_mode', e.target.checked ? '1' : '0'); } catch { /* ignore */ }
            }}
            className="w-3 h-3 accent-black"
          />
          🔒 Låsläge
        </label>
      </div>

      {/* Wake-up celebration overlay */}
      {wakeUp && (
        <FocusWakeUp
          pet={pet}
          pointsEarned={wakeUp.pointsEarned}
          leveledUp={wakeUp.leveledUp}
          onClose={() => setWakeUp(null)}
        />
      )}

      {/* Full-screen lock mode during work */}
      {lockMode && isRunning && isWork && (
        <FocusLockOverlay
          timeLeft={timeLeft}
          total={(timerType === 'custom' ? customMinutes : preset.work) * 60}
          petEmoji={pet?.emoji || '🐾'}
          onEmergencyExit={() => {
            setIsRunning(false);
            toast.info('Låsläge avbrutet. Det är okej 💛');
          }}
        />
      )}

      {/* Break exercises when work session ends */}
      {showBreakExercises && (
        <BreakExercises onClose={() => setShowBreakExercises(false)} />
      )}
    </div>
  );
}
